import { ref, reactive, watch, computed } from 'vue'
import { FormDataStructure } from '@/contracts/forms/FieldContracts'
import { NuxeoSearchRequest, NuxeoCalSearchRequest } from '@/contracts/api/RequestContracts'
import { NuxeoPageProviderResponse } from '@/contracts/api/ResponseContracts'
import { nuxeoSearch, nuxeoCalSearch } from '@/composables/nuxeo'
import { pageprovider, prepareSearchDates } from '@/composables/pageprovider'

// Prepare search methods and data
export function search(stageData: FormDataStructure, searchType: number)
{
    // Prepare for page provider:
    const {
        data,
        numberOfPaginationPages,
        currentPageIndex,
        sortDirection,
        sortColumn,
        prevPage,
        nextPage,
        goToPage
    } = pageprovider()

    // Set start and end search dates from day/month/year fields:
    const searchDates = computed(() =>
    {
        return prepareSearchDates(stageData.yearofdeath, stageData.monthofdeath, stageData.dayofdeath)
    })
    const probateDates = computed(() =>
    {
        return prepareSearchDates(stageData.yearofprobate, stageData.monthofprobate, stageData.dayofprobate)
    })
    // Prepare search params for request:
    const searchParams = computed(() =>
    {
        return {
            currentPageIndex: currentPageIndex.value,
            hmcts_grant_schema_grantdocTypeOf: stageData.grantType,
            hmcts_grant_schema_surname: (stageData.lastname ? stageData.lastname.toUpperCase() : ''),
            hmcts_grant_schema_dateofdeath_min: searchDates.value.searchDateFrom,
            hmcts_grant_schema_dateofdeath_max: searchDates.value.searchDateTo,
            hmcts_grant_schema_dateofprobate_min: probateDates.value.searchDateFrom,
            hmcts_grant_schema_dateofprobate_max: probateDates.value.searchDateTo,
            hmcts_grant_schema_firstnames: (stageData.firstname ? stageData.firstname.toUpperCase() : ''),
            hmcts_grant_schema_regimentnumber: stageData.regimentalnumber,
            sortBy: (sortColumn.value === 'none' ? '' : sortColumn.value),
            sortOrder: (sortDirection.value === 'ascending' ? 'ASC' : 'DESC')
        } as NuxeoSearchRequest
    })
    // Set month names:
    const monthNames =
    [
        'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
    ]
    // Prepare search params for calendar request:
    const searchCalParams = computed(() =>
    {
        const surname = (stageData.lastname ? stageData.lastname.toUpperCase() : '')
        const firstname = (stageData.firstname ? ' ' + stageData.firstname.toUpperCase() : '')
        const fullname = ' "' + surname + firstname + '"'
        const montofdeath = (stageData.monthofdeath ? ' ' + monthNames[parseInt(stageData.monthofdeath) - 1] : '')
        const dayofdeath = (stageData.dayofdeath ? ' ' + parseInt(stageData.dayofdeath) : '')
        const yearofprobate = (stageData.yearofprobate ? ' ' + stageData.yearofprobate : '')
        const monthofprobate = (stageData.monthofprobate ? ' ' + monthNames[parseInt(stageData.monthofprobate) - 1] : '')
        const dayofprobate = (stageData.dayofprobate ? ' ' + parseInt(stageData.dayofprobate) : '')
        const keywords = (stageData.keywords ? ' ' + stageData.keywords.replace(/[^a-z0-9 -]/gi, '') : '')
        return {
            currentPageIndex: currentPageIndex.value,
            queryParams:
            [
                'AND hmctscalindex:year = ' + stageData.yearofdeath
            ],
            textract_schema_docContent: stageData.yearofdeath + fullname + montofdeath + dayofdeath + yearofprobate + monthofprobate + dayofprobate + keywords,
            pageSize: 1
        } as NuxeoCalSearchRequest
    })

    // Watch to trigger search:
    watch(currentPageIndex, function()
    {
        runSearch()
    })
    watch(stageData, function()
    {
        runSearch()
    })
    // Run search:
    const runSearch = function()
    {
        // Run search:
        switch(searchType)
        {
            case 1:
                nuxeoSearch(searchParams.value,
                function(response: NuxeoPageProviderResponse)
                {
                    Object.assign(data, response)
                })
                break
            case 2:
                nuxeoCalSearch(searchCalParams.value,
                function(response: NuxeoPageProviderResponse)
                {
                    Object.assign(data, response)
                })
                break
        }
    }

    runSearch()

    return {
        data,
        numberOfPaginationPages,
        currentPageIndex,
        sortDirection,
        sortColumn,
        runSearch,
        prevPage,
        nextPage,
        goToPage
    }
}
